import React from 'react';
import { CONTACT_EMAIL } from './contact';

const Email: React.FC<{ subject: string }> = ({ subject }) => (
  <a
    className="legal-email"
    href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`}
  >
    {CONTACT_EMAIL}
  </a>
);

const ContactDetails: React.FC<{ subject: string }> = ({ subject }) => (
  <>
    <strong>Midnight Swim Ltd.</strong>
    {'\n'}
    Registered in England and Wales, company number 15376737
    {'\n'}
    <strong>Email:</strong> <Email subject={subject} />
  </>
);

export type LegalSubsection = {
  header: string;
  body: React.ReactNode;
};

export type LegalSection = {
  header: string;
  /** Prose that sits directly under the header. Omitted by sections that are
   *  only a heading over their subsections. */
  body?: React.ReactNode;
  subsections?: LegalSubsection[];
};

export type LegalDocKey = 'privacy' | 'gdpr' | 'terms';

export type LegalDoc = {
  key: LegalDocKey;
  title: string;
  /** Shown as a dateline under the title. Documents without one show none. */
  updated?: string;
  sections: LegalSection[];
};

export const privacyDoc: LegalDoc = {
  key: 'privacy',
  title: 'Privacy Policy',
  updated: '1 November 2026',
  sections: [
    {
      header: 'Who We Are',
      body: 'Midnight Swim Ltd. ("Midnight Swim", "we", "us" or "our") is a company registered in England and Wales under company number 15376737. We operate the mobile applications **Aquarist** and **Idle Café**, and we are the data controller for the personal data processed through them as described in this Policy.\n\nThis Privacy Policy explains what information the Apps and the third-party services they use collect, how that information is used and shared, how long it is retained, and the choices available to you.',
    },
    {
      header: 'Which Apps This Policy Covers',
      body: '**Aquarist** is an account-based fishkeeping app. It uses accounts and stores user-created data on our servers. It displays advertisements and uses analytics.\n\n**Idle Café** is a game. It does not require an account, and we do not store game accounts, profiles or game progress on our servers. Game progress is stored on your device. It displays advertisements and uses analytics.',
    },
    {
      header: 'What We Collect',
      subsections: [
        {
          header: 'Aquarist',
          body: 'Aquarist stores an account identifier, the details provided when you sign in, and the information you create in the App, including logged aquariums, planned aquariums, water readings, journals and planners.\n\nSign-in is provided by Apple or Google through **Firebase Authentication**. When you sign in, Apple or Google provides your email address and, depending on the service, your name and a link to your profile photo. These are stored with your account identifier. We do not use your name or profile photo in the App. Your email address is used to authenticate your account and to identify it if you contact us. If you sign in with Apple and choose Hide My Email, the address stored is the private relay address Apple creates for the App.\n\nFirebase also records when your account was created and when you last signed in.',
        },
        {
          header: 'Idle Café',
          body: 'Idle Café does not use accounts or profiles.\n\nGame progress is stored on your device rather than on our servers.',
        },
        {
          header: 'Analytics',
          body: 'Both Apps use third-party analytics provided by **Amplitude**.\n\nAnalytics events record how the Apps are used, such as interactions with features and controls. Events are associated with an app/device-scoped identifier so that activity from the same installation can be understood together.\n\nAnalytics does not use your advertising identifier.\n\nIn Aquarist, analytics events are linked to your account identifier once you sign in, so that errors and problems can be investigated.\n\nAnalytics data is retained for **six months after collection** and is then automatically deleted.',
        },
        {
          header: 'Advertising',
          body: 'Both Apps display advertisements using **Google AdMob**. We do not use any other advertising service.\n\nDepending on your privacy choices, Google may process information such as:\n• Device or advertising identifiers, where available and permitted\n• IP address and approximate location derived from it\n• Device type, operating system and app version\n• Information about advertisements requested, displayed or interacted with\n• Information about your interaction with the App relevant to advertising\n\nWe do not use precise GPS location for advertising.\n\nWhere personalised advertising is not permitted, non-personalised or Limited Ads may be served instead.',
        },
      ],
    },
    {
      header: 'Privacy Choices',
      body: "Where required, the Apps ask for your choices about how Google may use information and identifiers for advertising.\n\nOn iOS, Apple may separately ask for permission under App Tracking Transparency (ATT). This controls access to Apple's advertising identifier and related tracking activities.\n\nDeclining ATT does not prevent advertising from being displayed, and it does not prevent our separate product analytics from operating.\n\nYour advertising and privacy choices can be changed using the privacy controls provided by the App or your device.",
    },
    {
      header: 'How We Use Information',
      body: 'We use information to:\n• Provide and operate the Apps\n• Store and retrieve Aquarist account data\n• Understand how features are used and improve the Apps\n• Diagnose errors and technical problems\n• Provide and measure advertising\n• Maintain the security and reliability of the Apps\n• Comply with applicable legal obligations\n\nWhere we process personal data under applicable data protection law, we use an appropriate lawful basis for that processing. Where processing is based on consent, you may withdraw that consent at any time. Where processing is based on legitimate interests, you may have a right to object.',
    },
    {
      header: 'Who We Share Information With',
      body: 'We do not sell your personal data.\n\nWe share information with third-party service providers that provide services for the Apps, including:\n• **Google**, for advertising through Google AdMob\n• **Amplitude**, for product analytics\n• **Apple and Google**, for Aquarist sign-in\n• **Google Firebase**, for Aquarist authentication, including storing your account identifier and sign-in details\n• Our hosting and infrastructure providers, for storing and operating Aquarist account data\n\nThese providers process information under their own privacy policies and terms.\n\nWe may also disclose information where required by law or where necessary to protect our rights, users or services.\n\nSome service providers may process personal data outside the UK or European Economic Area. Where applicable, we use the transfer mechanisms and safeguards required by applicable data protection law.',
    },
    {
      header: 'How Long We Keep Data',
      subsections: [
        {
          header: 'Aquarist account data',
          body: 'Aquarist account data, including your sign-in details, is retained until you delete it or delete your account.',
        },
        {
          header: 'Analytics data',
          body: 'Analytics events for both Apps are retained for **six months after collection** and are then automatically deleted.\n\nDeleting an Aquarist account does not immediately delete analytics events already collected. Those events remain subject to the six-month retention period.',
        },
        {
          header: 'Advertising data',
          body: 'We do not retain advertising data ourselves. Google may retain information generated through advertising according to its own privacy policy and retention practices.',
        },
        {
          header: 'Idle Café game data',
          body: 'We do not retain Idle Café game progress on our servers. Game progress is stored on your device and is removed when you delete the App, subject to any device backups or other storage outside our control.',
        },
      ],
    },
    {
      header: 'Your Rights',
      body: (
        <>
          {
            'Depending on the applicable law and circumstances, you may have the right to:\n• Access your personal data\n• Correct inaccurate personal data\n• Request deletion of your personal data\n• Restrict certain processing\n• Object to processing based on legitimate interests\n• Receive certain personal data in a portable format\n• Withdraw consent where processing is based on consent\n\nYou can delete Aquarist account data directly within the App. If you cannot use the App, you can ask us to delete your account by emailing '
          }
          <Email subject="Aquarist account deletion request" /> from the email
          address associated with it.{'\n\n'}
          If you have a privacy request that cannot be handled within the App,
          contact us using the details below. We may need enough information to
          verify your request.{'\n\n'}
          We will respond to any request, including a request to delete your
          account, within one month of receiving it, or within one month of
          receiving any information we asked for to verify it.{'\n\n'}
          You also have the right to complain to the relevant data protection
          supervisory authority. In the UK, this is the{' '}
          <strong>Information Commissioner's Office (ICO)</strong>.
        </>
      ),
    },
    {
      header: 'Age Requirements',
      body: 'The Apps are intended for users aged 14 and over. They are not directed at or marketed to anyone under 14.\n\nWe do not knowingly collect personal data from anyone under 14. If we learn that we have, we will delete it. If you believe someone under 14 has provided us with personal data, please contact us.',
    },
    {
      header: 'Changes to This Privacy Policy',
      body: 'We may update this Privacy Policy from time to time.\n\nWhen we make material changes, we will update the date at the top of this Policy and, where appropriate, provide additional notice within the Apps.',
    },
    {
      header: 'Contact Us',
      body: <ContactDetails subject="Privacy Policy enquiry" />,
    },
  ],
};

export const gdprDoc: LegalDoc = {
  key: 'gdpr',
  title: 'GDPR Policy',
  updated: '1 November 2026',
  sections: [
    {
      header: 'Which Apps This Covers',
      body: 'Midnight Swim Ltd. operates two mobile applications: **Aquarist** and **Idle Café**.\n\nThe Apps handle data differently. Aquarist uses accounts and stores user-created data on our servers. Idle Café does not use accounts, and we do not store game progress or user profiles on our servers.',
    },
    {
      header: 'Aquarist: Your Data',
      body: "Aquarist stores an account identifier, the details provided when you sign in, and the information you create in the App, including:\n• Logged aquariums\n• Planned aquariums\n• Water readings\n• Journals\n• Planners\n\nSign-in is provided by Apple or Google through **Firebase Authentication** and is used to authenticate your account. When you sign in, Apple or Google provides your email address and, depending on the service, your name and a link to your profile photo. These are stored with your account identifier, along with the dates your account was created and last signed in. We do not use your name or profile photo in the App.\n\nAquarist displays advertisements provided by **Google AdMob**, which may use your device's advertising identifier where available and permitted.\n\nThe advertising identifier can be reset through your device settings. This affects the identifier available to advertising services and does not affect the identifiers used for product analytics.",
    },
    {
      header: 'Aquarist: Delete Specific Data',
      body: 'Aquariums, water readings, journals and planners can be deleted at any time using the delete controls provided within the App.',
    },
    {
      header: 'Aquarist: Delete Your Account',
      body: 'You can delete your Aquarist account and the data associated with it, including your sign-in details, from the Profile page using the **Delete Account** flow. This is the quickest way to delete your account, because being signed in already confirms that the account is yours.\n\nDeletion takes effect immediately on confirmation and removes the account data we hold.\n\nYour Apple or Google account is not affected because those accounts are controlled by Apple or Google, not Midnight Swim.',
    },
    {
      header: 'Aquarist: Delete Your Account by Email',
      body: (
        <>
          If you cannot use the in-app flow, email us at{' '}
          <Email subject="Aquarist account deletion request" /> from the email
          address associated with your account, and we will delete the account
          manually.{'\n\n'}
          If you signed in with Apple and chose Hide My Email, tell us the
          private relay address Apple created for Aquarist. You can find it in
          your Apple Account settings under Sign in with Apple.{'\n\n'}
          We may ask for further information to confirm that the account is
          yours before we delete it.{'\n\n'}
          We will delete your account within one month of receiving your
          request, or within one month of receiving any information we asked for
          to confirm the account is yours.
        </>
      ),
    },
    {
      header: 'Idle Café: Your Data',
      body: "Idle Café has no account or profile, and we do not store game progress on our servers.\n\nGame progress is stored on your device and is removed when the App is deleted, subject to any device backups or other storage outside our control.\n\nIdle Café displays advertisements provided by **Google AdMob**, which may use your device's advertising identifier where available and permitted.\n\nThe advertising identifier can be reset through your device settings. This affects the identifier available to advertising services and does not affect the separate identifier used for product analytics.",
    },
    {
      header: 'Analytics Retention',
      body: 'Analytics events for both Apps are retained for **six months after collection** and are then automatically deleted.\n\nDeleting an Aquarist account does not immediately delete analytics events already collected. Those events remain subject to the six-month retention period.',
    },
    {
      header: 'Your Rights',
      body: "Depending on the applicable law and circumstances, you may have the right to:\n• Access your personal data\n• Correct inaccurate personal data\n• Request deletion of your personal data\n• Restrict certain processing\n• Object to processing based on legitimate interests\n• Receive certain personal data in a portable format\n• Withdraw consent where processing is based on consent\n\nYou can delete Aquarist account data directly within the App.\n\nFor privacy requests that cannot be handled within the App, contact us using the details below. We may need enough information to verify your request.\n\nWe will respond to any request within one month of receiving it, or within one month of receiving any information we asked for to verify it.\n\nYou also have the right to complain to the relevant data protection supervisory authority. In the UK, this is the **Information Commissioner's Office (ICO)**.",
    },
    {
      header: 'Contact Us',
      body: <ContactDetails subject="GDPR Policy enquiry" />,
    },
  ],
};

export const termsDoc: LegalDoc = {
  key: 'terms',
  title: 'Terms and Conditions',
  updated: '1 November 2026',
  sections: [
    {
      header: 'Terms of Use',
      body: 'These Terms of Use ("Terms") govern your use of the mobile applications **Aquarist** and **Idle Café** (each an "App", and together the "Apps") provided by Midnight Swim Ltd. ("Midnight Swim", "we", "us" or "our").\n\nBy using an App, you agree to these Terms. If you do not agree with them, please do not use the App.',
    },
    {
      header: 'The Apps',
      body: '**Aquarist** is a fishkeeping application that requires a user account.\n\n**Idle Café** is a game that does not require or use user accounts.\n\nBoth Apps are supported by advertising.',
    },
    {
      header: 'Who Can Use the Apps',
      body: 'The Apps are intended for users aged 14 and over. You must be at least 14 years old to use them.',
    },
    {
      header: 'Aquarist Information',
      body: 'Aquarist provides information intended to help users care for aquariums and fish. This information is provided for general informational purposes only and is not professional veterinary, scientific or other specialist advice.\n\nFishkeeping requirements can vary between species, environments and individual circumstances. You should use your own judgement and, where appropriate, seek advice from a suitably qualified professional before making decisions that could affect the health or welfare of your animals.\n\nYou are responsible for decisions you make based on information provided through the App.',
    },
    {
      header: 'Accounts',
      body: 'This section applies to **Aquarist only**.\n\nAquarist requires you to sign in using Apple or Google. You are responsible for maintaining the security of the account you use to access Aquarist.\n\nYou may delete your Aquarist account and associated data at any time using the **Delete Account** function in the App. Details of account deletion and data retention are set out in our Privacy Policy.\n\nIdle Café does not use accounts.',
    },
    {
      header: 'Advertising',
      body: 'Both Apps display advertisements provided by Google AdMob.\n\nWe do not endorse or guarantee the products, services or claims contained in advertisements. Any transaction or other interaction you have with an advertiser is between you and that advertiser.\n\nOur use of information in connection with advertising is described in our Privacy Policy.',
    },
    {
      header: 'Third-Party Services and Links',
      body: "The Apps may contain links to or integrations with third-party websites, services or resources.\n\nThose third-party services are operated independently from Midnight Swim. We are not responsible for their content, availability or privacy practices.\n\nYour use of a third-party service may be subject to that provider's own terms and policies.",
    },
    {
      header: 'Intellectual Property',
      body: 'The Apps and their content, including software, text, graphics, images, logos and other materials, are owned by Midnight Swim or our licensors and are protected by applicable intellectual property laws.\n\nExcept as permitted by law or these Terms, you must not copy, reproduce, distribute, modify, create derivative works from, publicly display or commercially exploit the Apps or their content without our prior written permission.',
    },
    {
      header: 'Licence',
      body: "Subject to these Terms, Midnight Swim grants you a limited, personal, non-exclusive, non-transferable and revocable licence to use the Apps for their intended purposes.\n\nYou must not use the Apps for unlawful purposes or in a way that interferes with their operation or other users' ability to use them.",
    },
    {
      header: 'Restrictions on Use',
      body: 'You must not:\n• Sell, rent, sublicense, distribute or commercially exploit the Apps\n• Modify, decompile, disassemble or reverse engineer the Apps except where applicable law expressly permits this\n• Attempt to gain unauthorised access to the Apps, their systems or related services\n• Use the Apps to develop or provide a substantially similar or competing service',
    },
    {
      header: 'Availability and Changes',
      body: 'We may update, modify or discontinue features of an App from time to time.\n\nWe do not guarantee that an App or any particular feature will always be available, uninterrupted or error-free.\n\nWhere reasonably practicable, we will provide notice of significant changes or discontinuation.',
    },
    {
      header: 'Suspension and Termination',
      body: 'We may suspend or restrict access to an App where reasonably necessary, including where you materially breach these Terms, misuse the App, or where necessary to protect the security or operation of the App.\n\nFor Aquarist, suspension or termination may affect access to your account and user-created content.\n\nNothing in these Terms affects any rights you have under applicable consumer law.',
    },
    {
      header: 'Liability',
      body: 'Nothing in these Terms excludes or limits liability that cannot lawfully be excluded or limited.\n\nSubject to that, to the maximum extent permitted by applicable law, Midnight Swim will not be responsible for indirect or consequential loss arising from your use of the Apps.\n\nNothing in these Terms affects your statutory rights as a consumer, including rights relating to digital content and services.',
    },
    {
      header: 'Apple App Store',
      body: "Where you download an App through Apple's App Store, your use of the App is also subject to the applicable Apple Media Services Terms and Conditions.\n\nApple is not responsible for the App or its content and has no obligation to provide maintenance or support services for the App, except as required by applicable law.\n\nThese Terms are between you and Midnight Swim, not Apple.",
    },
    {
      header: 'Privacy',
      body: 'Your use of the Apps is also subject to our **Privacy Policy**, which explains how information is collected, used and shared.',
    },
    {
      header: 'Changes to These Terms',
      body: 'We may update these Terms from time to time.\n\nWhen we make material changes, we will update the date at the top of these Terms and, where appropriate, provide notice within the Apps.',
    },
    {
      header: 'Governing Law',
      body: 'These Terms are governed by the law of England and Wales.\n\nIf you live in England or Wales, you can bring legal proceedings about the Apps in the courts of England and Wales. If you live in Scotland, you can bring them in either the Scottish or the English courts. If you live in Northern Ireland, you can bring them in either the Northern Irish or the English courts.\n\nIf you live outside the UK, you also keep the protection of any mandatory consumer laws of the country where you live, and you may be able to bring proceedings in your local courts.',
    },
    {
      header: 'Contact Us',
      body: (
        <>
          Questions regarding these Terms may be sent to:{'\n\n'}
          <ContactDetails subject="Terms and Conditions enquiry" />
        </>
      ),
    },
  ],
};

export const legalDocs: Record<LegalDocKey, LegalDoc> = {
  privacy: privacyDoc,
  gdpr: gdprDoc,
  terms: termsDoc,
};
